/**
 * Does this org run on the built-in Borradh calendar?
 *
 * For a native org, availability comes from `shift` rows and bookings are
 * written straight to our own `appointment` table — there is no external
 * account to consult, so `primaryCalendarAccountId` is (and always will be)
 * null. Checking for that account id is what previously made every native org
 * look "unconfigured" to the availability and chatbot layers.
 *
 * A null `primaryCalendarType` is NOT native: that is the dominant production
 * state for orgs that send customers to an external booking system, and they
 * must keep doing exactly that.
 *
 * See ENG-500 for the data-model cleanup that should eventually replace this
 * predicate with an explicit availability-source field.
 */
export function usesNativeCalendar(org) {
    // `bookingDestination` is the field of record. `primaryCalendarType` is only
    // consulted when the caller has not selected the new column yet, which keeps
    // this correct during the expand/contract window rather than silently
    // reporting "not native" and switching an org's booking link.
    if (org.bookingDestination != null) {
        return org.bookingDestination === 'borradh';
    }
    return org.primaryCalendarType === 'borradh';
}
/**
 * Is this URL one of our own booking pages?
 *
 * Matches the host rather than a substring: "borradh.io.evil.com" and
 * "notborradh.io" must not qualify. Any subdomain of our domains counts, which
 * covers www/app and the staging/dev hosts.
 */
const OWN_BOOKING_DOMAINS = ['borrah.com', 'borradh.io'];
export function isBorradhBookingUrl(url) {
    try {
        const host = new URL(url.trim()).hostname.toLowerCase();
        return OWN_BOOKING_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
    }
    catch {
        // Unparseable link — treat as external, i.e. disqualifying.
        return false;
    }
}
/**
 * Does a customer who asks for the booking page get OUR page?
 *
 * The same two tests as the first half of `directBookingBlockedReason`, so the
 * link Claire sends and the diary tools she is given can never disagree. An org
 * whose destination still says `borradh` but which carries an external booking
 * link has its diary withheld by the gate; before this, every link-issuing path
 * read only the destination and handed that customer our booking page — a page
 * taking bookings into a diary the clinic does not use (Rachel Aesthetics,
 * 2026-10-09..11: diary gated on 34 turns, our page sent four times).
 *
 * The custom-prompt test is deliberately not repeated here: it names a
 * booking system without giving us a link to send instead.
 */
export function sendsToBorradhBookingPage(org) {
    if (!usesNativeCalendar(org))
        return false;
    return !org.defaultBookingLink || isBorradhBookingUrl(org.defaultBookingLink);
}
//# sourceMappingURL=booking-destination.js.map